import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUserState } = useAuth();
  const [unreadResults, setUnreadResults] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Get localStorage key for current user
  const getStorageKey = useCallback(() => {
    return currentUserState ? `unread-results-${currentUserState.uid}` : null;
  }, [currentUserState]);

  // Load unread results from localStorage
  const loadUnreadResults = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) return new Set();

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const unreadArray = JSON.parse(stored);
        return new Set(unreadArray);
      }
    } catch (error) {
      // Silently fail - localStorage errors are non-critical
    }
    return new Set();
  }, [getStorageKey]);

  // Save unread results to localStorage
  const saveUnreadResults = useCallback(
    (unreadSet) => {
      const storageKey = getStorageKey();
      if (!storageKey) return;

      try {
        const unreadArray = Array.from(unreadSet);
        localStorage.setItem(storageKey, JSON.stringify(unreadArray));
      } catch (error) {
        // Silently fail - localStorage errors are non-critical
      }
    },
    [getStorageKey]
  );

  // Initialize unread results when user changes
  useEffect(() => {
    if (currentUserState) {
      const stored = loadUnreadResults();
      setUnreadResults(stored);
      setIsInitialized(true);
    } else {
      setUnreadResults(new Set());
      setIsInitialized(false);
    }
  }, [currentUserState, loadUnreadResults]);

  // Mark a result as read
  const markAsRead = useCallback(
    (resultId) => {
      setUnreadResults((prev) => {
        const newSet = new Set(prev);
        newSet.delete(resultId);
        saveUnreadResults(newSet);
        return newSet;
      });
    },
    [saveUnreadResults]
  );

  // Mark a result as unread (when new result comes in)
  const markAsUnread = useCallback(
    (resultId) => {
      setUnreadResults((prev) => {
        const newSet = new Set(prev);
        newSet.add(resultId);
        saveUnreadResults(newSet);
        return newSet;
      });
    },
    [saveUnreadResults]
  );

  // Mark multiple results as unread (when new results are detected)
  const markMultipleAsUnread = useCallback(
    (resultIds) => {
      setUnreadResults((prev) => {
        const newSet = new Set(prev);
        resultIds.forEach((id) => newSet.add(id));
        saveUnreadResults(newSet);
        return newSet;
      });
    },
    [saveUnreadResults]
  );

  // Check if a specific result is unread
  const isUnread = useCallback(
    (resultId) => {
      return unreadResults.has(resultId);
    },
    [unreadResults]
  );

  // Check if there are any unread results
  const hasUnreadResults = unreadResults.size > 0;

  // Get count of unread results
  const unreadCount = unreadResults.size;

  // Sync with new shared quiz results - call this when new results are detected
  const syncWithSharedResults = useCallback(
    (currentSharedResults, previousSharedResults = []) => {
      // Find new results that weren't there before
      const newResults = currentSharedResults.filter(
        (result) => !previousSharedResults.some((prev) => prev.id === result.id)
      );

      if (newResults.length > 0) {
        const newResultIds = newResults.map((result) => result.id);
        markMultipleAsUnread(newResultIds);

        // Return new results for potential push notification
        return newResults;
      }

      return [];
    },
    [markMultipleAsUnread]
  );

  // Clear all unread results
  const clearAll = useCallback(() => {
    setUnreadResults(new Set());
    const storageKey = getStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [getStorageKey]);

  const value = {
    unreadResults,
    hasUnreadResults,
    unreadCount,
    isInitialized,
    markAsRead,
    markAsUnread,
    markMultipleAsUnread,
    isUnread,
    syncWithSharedResults,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
